import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layers, Users, ArrowRight, Wifi, Info, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { Queues, type LeaderAgentRow, type QueueInfo } from '@/lib/api';
import { useLeaderAgents } from '@/lib/useLeaderAgents';
import { useBackendOnline } from '@/store/connection';

/**
 * Takım Lideri → KUYRUK DURUMU. Eskiden "Canlı Çağrılar" placeholder'ı vardı;
 * ancak sistem-geneli aktif çağrı listesi / kuyruk bekleme derinliği 3CX xapi
 * ile ALINAMIYOR (3CX DB erişimi gerekir). xapi ile alınabilen GERÇEK kapsam:
 * her kuyruğun üye listesi + kaçının login olduğu + üyelerin anlık durumu.
 *
 * Bu sayfa kuyruk başına: kuyrukta(login) / üye sayısı + üye durum dağılımı
 * (hazır/molada/kuyruk-dışı/çevrimdışı) gösterir. Kuyruk üyeleri (3CX /Queues)
 * leader agents (3CX durum + activity_logs) ile extension üzerinden eşleştirilir.
 */
const BREAK_STATUSES = ['mola', 'yemek', 'dis-arama', 'ozel'];

type Bucket = 'hazir' | 'mola' | 'kuyrukDisi' | 'cevrimdisi' | 'harici';

const BUCKET_META: Record<Bucket, { label: string; bar: string; dot: string }> = {
  hazir:      { label: 'Hazır',       bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
  mola:       { label: 'Molada',      bar: 'bg-amber-500',   dot: 'bg-amber-500' },
  kuyrukDisi: { label: 'Kuyruk dışı', bar: 'bg-slate-400',   dot: 'bg-slate-400' },
  cevrimdisi: { label: 'Çevrimdışı',  bar: 'bg-slate-300',   dot: 'bg-slate-300' },
  harici:     { label: 'Harici',      bar: 'bg-slate-200',   dot: 'bg-slate-300' },
};
const BUCKET_ORDER: Bucket[] = ['hazir', 'mola', 'kuyrukDisi', 'cevrimdisi', 'harici'];

interface QueueView extends QueueInfo {
  buckets: Record<Bucket, number>;
  /** Eşleşen (ArkConnect kullanıcısı) üyeler — avatar şeridi için. */
  known: { ext: string; agent: LeaderAgentRow; bucket: Bucket }[];
  externalCount: number;
}

function bucketOf(a: LeaderAgentRow): Bucket {
  if (BREAK_STATUSES.includes(a.status)) return 'mola';
  if (a.status === 'hazir') return 'hazir';
  if (!a.online || a.status === 'cevrimdisi') return 'cevrimdisi';
  return 'kuyrukDisi';
}

export default function QueueStatus() {
  const backendOnline = useBackendOnline();
  const { data: agents } = useLeaderAgents();
  const {
    data: queues, isLoading, isError, refetch, isFetching,
  } = useQuery<QueueInfo[]>({
    queryKey: ['leader-queues'],
    queryFn: Queues.all,
    refetchInterval: 20_000,
    staleTime: 10_000,
    enabled: backendOnline,
  });

  const byExt = useMemo(
    () => new Map((agents ?? []).map((a) => [a.extension, a])),
    [agents],
  );

  const views = useMemo<QueueView[]>(() => {
    return (queues ?? []).map((q) => {
      const buckets: Record<Bucket, number> = { hazir: 0, mola: 0, kuyrukDisi: 0, cevrimdisi: 0, harici: 0 };
      const known: QueueView['known'] = [];
      for (const ext of q.members ?? []) {
        const a = byExt.get(ext);
        if (!a) { buckets.harici += 1; continue; }
        const b = bucketOf(a);
        buckets[b] += 1;
        known.push({ ext, agent: a, bucket: b });
      }
      // Hazır → çevrimdışı sırasıyla diz (glance'ta önce müsaitler).
      known.sort((x, y) => BUCKET_ORDER.indexOf(x.bucket) - BUCKET_ORDER.indexOf(y.bucket));
      return { ...q, buckets, known, externalCount: buckets.harici };
    }).sort((a, b) => b.agentCount - a.agentCount);
  }, [queues, byExt]);

  const totals = useMemo(() => ({
    queues: views.length,
    members: views.reduce((s, q) => s + q.agentCount, 0),
    loggedIn: views.reduce((s, q) => s + q.loggedInCount, 0),
    hazir: views.reduce((s, q) => s + q.buckets.hazir, 0),
  }), [views]);

  return (
    <div className="px-5 py-3 max-w-[1300px] mx-auto">
      {/* Header */}
      <header className="flex items-end justify-between mb-2.5 gap-3">
        <div>
          <h1 className="text-[18px] font-semibold tracking-tight text-slate-900 leading-tight flex items-center gap-2">
            <Layers size={17} className="text-brand-600" /> Kuyruk Durumu
          </h1>
          <div className="text-[11.5px] text-slate-400 mt-0.5">
            Her kuyruğun üye sayısı, kuyruğa login üyeler ve anlık durum dağılımı
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium bg-white ring-1 ring-slate-200 text-slate-600 hover:bg-slate-50 transition"
          >
            <RefreshCw size={13} className={clsx(isFetching && 'animate-spin')} /> Yenile
          </button>
          <Link
            to="/agents"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-brand-500 hover:bg-brand-600 text-[#08140d] shadow-[0_4px_12px_-3px_rgba(255,79,31,0.5)] transition"
          >
            <Users size={13} /> Temsilciler <ArrowRight size={13} />
          </Link>
        </div>
      </header>

      {/* Özet strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-2.5">
        <MiniStat icon={<Layers size={15} />}  tone="slate"   label="Kuyruk"        value={totals.queues} loading={isLoading} />
        <MiniStat icon={<Users size={15} />}   tone="slate"   label="Toplam üye"    value={totals.members} loading={isLoading} />
        <MiniStat icon={<Wifi size={15} />}    tone="sky"     label="Kuyrukta"      value={totals.loggedIn} loading={isLoading} />
        <MiniStat icon={<Wifi size={15} />}    tone="emerald" label="Hazır üye"     value={totals.hazir} loading={isLoading} />
      </div>

      {/* xapi kısıt notu — beklenti yönetimi */}
      <div className="flex items-start gap-2 text-[11.5px] text-slate-500 bg-slate-50 ring-1 ring-slate-200/70 rounded-xl px-3 py-2 mb-2.5">
        <Info size={14} className="text-slate-400 mt-0.5 shrink-0" />
        <span>
          Anlık <strong>bekleyen çağrı sayısı</strong> ve kuyruk bekleme süresi 3CX xapi ile
          alınamıyor (3CX veritabanı erişimi gerekir). Bu sayfa kuyruk <strong>üye durumunu</strong> gösterir.
        </span>
      </div>

      {isError && (
        <div className="text-[12.5px] text-red-600 bg-red-50 ring-1 ring-red-200 rounded-xl px-4 py-3 mb-2.5">
          Kuyruk verisi alınamadı — 3CX bağlantısını kontrol et.
        </div>
      )}

      {/* Kuyruk kartları */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-[150px] rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : views.length === 0 ? (
        <div className="py-16 text-center text-[12.5px] text-slate-400">Tanımlı kuyruk bulunamadı.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
          {views.map((q) => <QueueCard key={q.id} q={q} />)}
        </div>
      )}
    </div>
  );
}

/* ─── Kuyruk kartı ────────────────────────────────────────────── */

function QueueCard({ q }: { q: QueueView }) {
  const segments = BUCKET_ORDER
    .map((b) => ({ b, n: q.buckets[b] }))
    .filter((s) => s.n > 0);
  const empty = q.agentCount === 0;

  return (
    <section className="rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3.5 shadow-sm flex flex-col gap-2.5">
      {/* Başlık satırı */}
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
          <Layers size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-slate-900 truncate leading-tight">{q.name}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[10.5px] text-slate-400 font-mono">#{q.number}</span>
            {q.pollingStrategy && (
              <span className="text-[9.5px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                {q.pollingStrategy}
              </span>
            )}
          </div>
        </div>
        {/* Kuyrukta / üye */}
        <div className="text-right shrink-0">
          <div className="text-[20px] font-bold text-slate-900 tabular-nums leading-none">
            {q.loggedInCount}<span className="text-slate-300 font-medium"> / {q.agentCount}</span>
          </div>
          <div className="text-[9.5px] font-medium text-slate-400 uppercase tracking-wide mt-0.5">kuyrukta / üye</div>
        </div>
      </div>

      {empty ? (
        <div className="py-4 text-center text-[11.5px] text-slate-400">Bu kuyruğa atanmış üye yok.</div>
      ) : (
        <>
          {/* Durum dağılım çubuğu */}
          <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
            {segments.map((s) => (
              <div
                key={s.b}
                className={BUCKET_META[s.b].bar}
                style={{ width: `${(s.n / q.agentCount) * 100}%` }}
                title={`${BUCKET_META[s.b].label}: ${s.n}`}
              />
            ))}
          </div>

          {/* Legend (sadece >0 olanlar) */}
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {segments.map((s) => (
              <span key={s.b} className="inline-flex items-center gap-1.5 text-[11px] text-slate-600">
                <span className={clsx('w-2 h-2 rounded-full', BUCKET_META[s.b].dot)} />
                {BUCKET_META[s.b].label}
                <strong className="tabular-nums text-slate-900">{s.n}</strong>
              </span>
            ))}
          </div>

          {/* Üye avatar şeridi (eşleşen kullanıcılar) */}
          {q.known.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {q.known.map(({ ext, agent, bucket }) => (
                <div
                  key={ext}
                  title={`${agent.displayName} (${ext}) — ${BUCKET_META[bucket].label}`}
                  className="relative w-7 h-7 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 text-white flex items-center justify-center text-[9.5px] font-semibold shrink-0"
                >
                  {agent.displayName.slice(0, 2).toUpperCase()}
                  <span className={clsx('absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-base', BUCKET_META[bucket].dot)} />
                </div>
              ))}
              {q.externalCount > 0 && (
                <div
                  title={`${q.externalCount} harici üye (ArkConnect kullanıcısı değil)`}
                  className="h-7 px-2 rounded-full bg-slate-100 text-slate-500 flex items-center text-[10.5px] font-medium shrink-0"
                >
                  +{q.externalCount} harici
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/* ─── Özet mini stat ──────────────────────────────────────────── */

function MiniStat({ icon, tone, label, value, loading }: {
  icon: React.ReactNode; tone: 'slate' | 'sky' | 'emerald';
  label: string; value: number; loading?: boolean;
}) {
  const iconCls = {
    slate: 'bg-slate-100 text-slate-600', sky: 'bg-sky-50 text-sky-600', emerald: 'bg-emerald-50 text-emerald-600',
  }[tone];
  return (
    <div className="rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 shadow-sm flex items-center gap-2.5">
      <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0', iconCls)}>{icon}</div>
      <div className="min-w-0">
        <div className="text-[22px] font-bold text-slate-900 tabular-nums leading-none">{loading ? '–' : value}</div>
        <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mt-0.5 truncate">{label}</div>
      </div>
    </div>
  );
}
